import React, { useState, useEffect } from 'react';
import { Alert } from '../../../utils/Alert';
import Navbar from '../../../components/layout/Navbar';
import '../Admin.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  LabelList
} from 'recharts';
import AdminSidebar from '../AdminSidebar';
import axios from 'axios';
import * as XLSX from 'xlsx';
import MainLayout from '../../../components/layout/MainLayout';
import { FaReceipt } from 'react-icons/fa';
import { API_URL } from '../../../config';

interface DashboardPageProps {
  user: {
    name: string;
    role: string;
    avatar?: string;
    [key: string]: any;
  };
  onLogout: () => void;
}

interface SubscriptionBreakdown {
  subscription_id: number;
  name: string;
  total_users: number;
  active_users: number;
  revenue: number;
}

interface SubscriptionOverview {
  total_subscriptions: number;
  active_subscriptions: number;
  expired_subscriptions: number;
  cancelled_subscriptions: number;
  total_revenue: number;
  subscription_breakdown: SubscriptionBreakdown[];
}

const SubscriptionReports: React.FC<DashboardPageProps> = ({ user, onLogout }) => {
  const [timePeriod, setTimePeriod] = useState<string>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SubscriptionOverview | null>(null);

  useEffect(() => {
    fetchSubscriptionData();
  }, [timePeriod]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/reports/subscriptions/overview`, {
        params: { time_period: timePeriod },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setData(response.data);
      setError(null);
    } catch (err: any) {
      Alert.error('Oops...', 'Failed to fetch subscription data. Please try again later.');
      console.error('Error fetching subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = () => {
    if (!data) return;

    const csvContent = [
      ['Subscription Report'],
      ['Time Period:', timePeriod],
      [''],
      ['Overall Statistics'],
      ['Total Subscriptions:', data.total_subscriptions],
      ['Active Subscriptions:', data.active_subscriptions],
      ['Expired Subscriptions:', data.expired_subscriptions],
      ['Cancelled Subscriptions:', data.cancelled_subscriptions],
      ['Total Revenue:', calculateTotalRevenue(data) > 0 ? `$${calculateTotalRevenue(data)}` : calculateTotalRevenue(data)],
      [''],
      ['Subscription Breakdown'],
      ['Name', 'Total Users', 'Active Users', 'Revenue'],
      ...data.subscription_breakdown.map(sub => [
        sub.name,
        sub.total_users,
        sub.active_users,
        sub.name.toLowerCase() === 'free' ? 0 : (sub.revenue > 0 ? `$${sub.revenue}` : sub.revenue)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `subscription_report_${timePeriod}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const calculateTotalRevenue = (data: SubscriptionOverview) => {
    return data.subscription_breakdown.reduce((total, sub) => {
      if (sub.name.toLowerCase() === 'free') {
        return total;
      }
      return total + sub.revenue;
    }, 0);
  };

  const exportToExcel = () => {
    if (!data) return;

    // Prepare the data for export
    const subscriptionData = data.subscription_breakdown.map(sub => ({
      'Subscription Name': sub.name,
      'Total Users': sub.total_users,
      'Active Users': sub.active_users,
      'Revenue': sub.name.toLowerCase() === 'free' ? 0 : sub.revenue
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Add overall statistics
    const overallStats = [
      ['Subscription Report Overview'],
      ['Time Period:', timePeriod],
      [''],
      ['Overall Statistics'],
      ['Total Subscriptions:', data.total_subscriptions],
      ['Active Subscriptions:', data.active_subscriptions],
      ['Expired Subscriptions:', data.expired_subscriptions],
      ['Cancelled Subscriptions:', data.cancelled_subscriptions],
      ['Total Revenue:', calculateTotalRevenue(data)],
      ['']
    ];
    
    // Convert overall stats to worksheet
    const ws1 = XLSX.utils.aoa_to_sheet(overallStats);
    XLSX.utils.book_append_sheet(wb, ws1, 'Overview');

    // Convert subscription data to worksheet
    const ws2 = XLSX.utils.json_to_sheet(subscriptionData);
    XLSX.utils.book_append_sheet(wb, ws2, 'Subscription Details');

    // Generate Excel file
    const fileName = `subscription_report_${timePeriod}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <MainLayout user={user} onLogout={onLogout} sidebarContent={<AdminSidebar />}>  
    <div className="min-h-screen bg-gray-50 font-kumbh">
      <div className="flex min-h-screen">
        
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h1 className="admin-title">Subscription Reports</h1>
              </div>
              <div className="flex gap-4">
                <select
                  className="form-select px-3 py-2 text-base font-normal text-gray-600 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-600 focus:bg-white focus:border-[#111827] focus:outline-none"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(e.target.value)}
                >
                  <option value="all">All Time</option>
                  <option value="month">Last Month</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                </select>
                <button
                  onClick={exportToExcel}
                  disabled={!data || loading}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    !data || loading
                      ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  Export to Excel
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#111827] mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading subscription data...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <p className="text-red-700">{error}</p>
              </div>
            ) : data && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-600">Total Subscriptions</h3>
                    <p className="text-2xl font-bold text-gray-900">
                      {data.total_subscriptions.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-600">Active Subscriptions</h3>
                    <p className="text-2xl font-bold text-gray-900">
                      {data.active_subscriptions.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-600">Total Revenue</h3>
                    <p className="text-2xl font-bold text-gray-900">
                      {calculateTotalRevenue(data) > 0 ? `Rs${calculateTotalRevenue(data).toLocaleString()}` : calculateTotalRevenue(data).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-600">Cancelled Subscriptions</h3>
                    <p className="text-2xl font-bold text-gray-900">
                      {data.cancelled_subscriptions.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg font-medium text-gray-900">Subscription Breakdown</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Total Users</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Active Users</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {data.subscription_breakdown.map((subscription) => (
                          <tr key={subscription.subscription_id}>
                            <td className="px-8 py-6 whitespace-nowrap text-sm font-medium text-gray-900">{subscription.name}</td>
                            <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-600">{subscription.total_users}</td>
                            <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-600">{subscription.active_users}</td>
                            <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-600">
                              {subscription.name.toLowerCase() === 'free' ? 0 : (subscription.revenue > 0 ? `$${subscription.revenue.toLocaleString()}` : subscription.revenue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Data Visualization Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">

                  {/* Revenue by Subscription Type — Horizontal Bar (single solid color, fixed height) */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col min-w-0">
                    <div className="mb-4">
                      <h3 className="text-base font-semibold text-gray-900">Revenue by Subscription Type</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Earnings sorted per active package (INR)</p>
                    </div>
                    <ResponsiveContainer width="99%" height={380}>
                      <BarChart
                        data={[...data.subscription_breakdown]
                          .filter(s => s.name.toLowerCase() !== 'free')
                          .sort((a, b) => b.revenue - a.revenue)}
                        layout="vertical"
                        margin={{ left: 8, right: 24, top: 4, bottom: 24 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          axisLine={false} tickLine={false}
                          tickFormatter={v => v >= 1000 ? `₹${v/1000}k` : `₹${v}`}
                          label={{ value: 'Revenue Generated (₹)', position: 'insideBottom', offset: -14, fontSize: 11, fill: '#94a3b8' }}
                        />
                        <YAxis
                          type="category" dataKey="name"
                          tick={{ fontSize: 11, fill: '#334155', fontWeight: 500 }}
                          width={150} axisLine={false} tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: 12, background: '#0f172a', color: '#fff' }}
                          labelStyle={{ color: '#fff', fontWeight: 600 }}
                          itemStyle={{ color: '#e2e8f0' }}
                          formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, 'Total']}
                          cursor={{ fill: 'rgba(79,70,229,0.06)' }}
                        />
                        <Bar dataKey="revenue" radius={[0, 6, 6, 0]} barSize={18} fill="#4f46e5" minPointSize={4}>
                          <LabelList
                            dataKey="revenue"
                            position="right"
                            style={{ fontSize: 11, fill: '#64748b' }}
                            formatter={(v: number) => v >= 1000 ? `₹${(v/1000).toFixed(1)}k` : `₹${v}`}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* User Distribution — Doughnut + custom bottom legend (no overflow) */}
                  {(() => {
                    const CLRS = ['#0ea5e9','#6366f1','#8b5cf6','#ec4899','#f43f5e','#f97316','#eab308','#10b981','#06b6d4','#3b82f6','#94a3b8','#14b8a6','#a855f7','#f59e0b'];
                    const total = data.subscription_breakdown.reduce((s, d) => s + d.active_users, 0);
                    return (
                      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col min-w-0">
                        <div className="mb-4">
                          <h3 className="text-base font-semibold text-gray-900">User Distribution</h3>
                          <p className="text-xs text-gray-400 mt-0.5">Active user volume by registered course category</p>
                        </div>
                        {/* Donut — fixed height, centered */}
                        <ResponsiveContainer width="99%" height={260}>
                          <PieChart>
                            <Pie
                              data={data.subscription_breakdown}
                              dataKey="active_users"
                              nameKey="name"
                              cx="50%" cy="50%"
                              innerRadius={70} outerRadius={110}
                              paddingAngle={2}
                            >
                              {data.subscription_breakdown.map((_, i) => (
                                <Cell key={i} fill={CLRS[i % CLRS.length]} stroke="#fff" strokeWidth={2} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: 12, background: '#0f172a', color: '#fff' }}
                              labelStyle={{ color: '#fff', fontWeight: 600 }}
                              itemStyle={{ color: '#e2e8f0' }}
                              formatter={(v: number, name: any) => {
                                const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0.0';
                                return [`${v} users (${pct}%)`, name];
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        {/* Custom legend — wraps inside card, never overflows */}
                        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
                          {data.subscription_breakdown.map((item, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0 inline-block" style={{ background: CLRS[i % CLRS.length] }} />
                              <span className="text-xs text-slate-600">{item.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Subscription Status Overview */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 lg:col-span-2">
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-800">Subscription Status Overview</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Active vs Expired vs Cancelled</p>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={[
                          { name: 'Active', value: data.active_subscriptions },
                          { name: 'Expired', value: data.expired_subscriptions },
                          { name: 'Cancelled', value: data.cancelled_subscriptions }
                        ]}
                        margin={{ top: 4, right: 20, left: 0, bottom: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }}
                        />
                        <Bar dataKey="value" name="Subscriptions" radius={[5, 5, 0, 0]} barSize={48}>
                          <Cell fill="#6366f1" />
                          <Cell fill="#f59e0b" />
                          <Cell fill="#ef4444" />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </MainLayout>
  );
};

export default SubscriptionReports; 
